import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, ResolveFn, Router } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { catchError, firstValueFrom, forkJoin, Observable, of, switchMap, throwError } from "rxjs";
import { ConfigService } from "src/app/servicios/config/config.service";
import { RESPONSE } from "src/express.tokens";
import { DetalleNoticiasService } from "../../servicios/detalle-noticias/detalle-noticias.service";
import { PlatformService } from "../../servicios/platform/platform.service";
import { ComentariosService } from "@src/app/servicios/comentarios/comentarios.service";

let comentariosService;
let detalleNoticiasService;
let constantesGlobales;
let platformService;
let router;
let response;

/**
 * Resolver para la página de detalle de noticia. Esto se encarga de obtener la información necesaria para la página de detalle de noticia
 * antes de que se cargue la página.
 * @param {ActivatedRouteSnapshot} route
 * @returns {Observable<any>} - Un observable con la información necesaria para la página de detalle de noticia.
 */

export const VideoResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot): Observable<any> => {
  platformService = inject(PlatformService);
  if (platformService.isBrowser()) {
    router = inject(Router);
  } else {
    response = inject(RESPONSE, { optional: true });
  }
  const cookieService = inject(CookieService);
  const configService = inject(ConfigService);

  constantesGlobales = configService.getConstantesGlobales();

  const idParam = route.paramMap.get("id");
  const search = isNaN(+idParam) ? idParam : +idParam;
  const by = isNaN(+idParam) ? "slug" : "id";

  const urlFrom = route.paramMap.get("id3") === "isquadPrensa" ? route.paramMap.get("id3") : "";

  comentariosService = inject(ComentariosService);
  detalleNoticiasService = inject(DetalleNoticiasService);
  let userLogin = false;
  if (platformService.isBrowser()) {
    userLogin = cookieService.get("userLogin") ? true : false;
  }
  return makeApiCalls(search, by, userLogin, urlFrom);
};

/**
 * Realiza las llamadas a la API necesarias para obtener la información necesaria para la página de detalle de noticia.
 * @param {string | number} search - El valor de búsqueda.
 * @param {string} by - El tipo de búsqueda.
 * @param {boolean} userLogin - Si el usuario está logueado.
 * @param {string} urlFrom - La url de la que proviene.
 * @returns {Observable<any>} - Un observable con la información necesaria para la página de detalle de noticia.
 */
function makeApiCalls(search, by, userLogin, urlFrom): Observable<any> {
  const videoSubChannel = detalleNoticiasService.getVideoSubcanal(search, constantesGlobales.idAmbito, by);
  if (videoSubChannel) {
    const combinedCalls = videoSubChannel.pipe(
      switchMap((subcanal) => {
        const calls = {
          ultimasNoticiasSeccion: firstValueFrom(detalleNoticiasService.getNoticiasSeccionPrensa(subcanal))
            .then((result) => {
              return result ? result : null;
            })
            .catch(() => {
              return of(null);
            }),
          relacionadas: firstValueFrom(detalleNoticiasService.getNoticiasRelacionadas(search))
            .then((result) => {
              return result ? result : null;
            })
            .catch(() => {
              return of(null);
            }),
          nextPrev: firstValueFrom(detalleNoticiasService.getNextPrevPrensa(search))
            .then((result) => {
              return result ? result : null;
            })
            .catch(() => {
              return of(null);
            }),
          comentarios: firstValueFrom(comentariosService.getComentariosPrensa(search))
            .then((result) => {
              return result ? result : null;
            })
            .catch(() => {
              return of(null);
            }),
          video: firstValueFrom(detalleNoticiasService.getVideoPrensa(constantesGlobales.idAmbito, search, "noticia", by, userLogin, urlFrom))
            .then((result) => {
              return result ? result : null;
            })
            .catch(() =>
              throwError(() => {
                // Control de redirección a 404 dependiendo de si se está en el navegador o en el servidor, de esta forma se evita el error de redirección en el servidor
                // y se redirige con un 301 gracias a la respuesta del servidor.
                if (platformService.isBrowser()) {
                  router.navigateByUrl("/404");
                } else if (response && !platformService.isBrowser()) {
                  response.redirect(301, "/404");
                }
              }),
            ),
        };
        return forkJoin(calls);
      }),
      catchError(() => {
        // Si hay un error en la petición, se devuelve un objeto con valores nulos.
        return of({ ultimasNoticiasSeccion: null, relacionadas: null, nextPrev: null, video: null });
      }),
    );
    return combinedCalls;
  }
}
